// Modelos 3D: dispositivos de entrada (joystick, encoder, teclados, receptor IR con su control) y
// la báscula HX711. Los sensores I²C están en inputs-i2c.js.
import * as THREE from 'three';
import { Builder, M, mat, text, extrudeBoard, boardShape, glow, setGlow } from '../kit.js';
import {
  JOYSTICK, ENCODER, KEYPAD, keypadLayout, keypadPins, keypadConnectorZ,
  IR_REMOTE, IR_REMOTE_KEYS, HX711_BOARD, LOAD_CELL, HX711_CELLS,
} from '../../parts/inputs.js';
import { PITCH, SILK, pcb, pad, headerUp, headerSide, smd, emptyPads, soic, sot23, cable, sharedTexture, sharedGeo } from './inputs-common.js';
import { MODELS as I2C } from './inputs-i2c.js';

/** Marca una malla (y sus hijas) como control interactivo. */
function interactive(obj, data) {
  obj.traverse((o) => {
    if (o.isMesh) o.userData.interactive = data;
  });
}

/** Textura de rayas verticales (moleteado de perillas y encoders). */
function knurlTexture(key, base, line, n, band = [0, 1]) {
  return sharedTexture('knurl:' + key, 32, 8, 8, (g) => {
    g.fillStyle = base;
    g.fillRect(0, 0, 32, 8);
    g.fillStyle = line;
    const y0 = (1 - band[1]) * 8;
    const h = (band[1] - band[0]) * 8;
    for (let i = 0; i < n; i++) g.fillRect((i * 32) / n, y0, 32 / n / 2, h);
  });
}

// Joystick KY-023 -----------------------------------------------------------------------------------
function buildJoystick() {
  const J = JOYSTICK;
  const b = new Builder();
  const top = J.top;
  const sx = J.stickX;
  const names = ['GND', '+5V', 'VRx', 'VRy', 'SW'];
  const zs = names.map((_, i) => (i - 2) * PITCH);
  pcb(b, {
    key: 'ky023', w: J.w, d: J.d, y0: J.pcbY, color: '#16181b', edge: '#0d0e10',
    holes: [[-14.2, -10.4, 1.5], [-14.2, 10.4, 1.5], [14.2, -10.4, 1.5], [14.2, 10.4, 1.5]],
    draw: (g, X, Y) => {
      names.forEach((n, i) => text(g, n, X(-13.4), Y(zs[i]), { size: 1.35, color: SILK, align: 'left', weight: 700 }));
      g.strokeStyle = SILK;
      g.lineWidth = 0.2;
      g.strokeRect(X(sx - 9.4), Y(-9.4), 18.8, 18.8);
      text(g, 'R1', X(-8.1), Y(10.7), { size: 1.1, color: SILK });
      text(g, 'KY-023', X(sx), Y(11.4), { size: 1.25, color: SILK, weight: 700 });
    },
  });
  headerSide(b, zs, { bendX: -14.7, tipX: J.pinX, y: top + 1.27, top, dir: -1 });
  emptyPads(b, -6.0, 10.7, top, { rot: 0 }); // R1: pull-up de SW sin soldar
  // Base de la palanca: caja negra, potenciómetros en dos lados y armazón metálico.
  const plastic = mat('#202124', { roughness: 0.5 });
  b.box(16, 4.6, 16, plastic, sx, top, 0);
  b.box(10.5, 9.2, 3.0, M.blackPlastic, sx - 0.6, top, -9.5); // potenciómetro del eje X
  b.box(3.0, 9.2, 10.5, M.blackPlastic, sx + 9.5, top, -0.6); // potenciómetro del eje Y
  b.box(7.4, 0.5, 2.0, M.steel, sx - 0.6, top + 9.2, -9.5);
  b.box(2.0, 0.5, 7.4, M.steel, sx + 9.5, top + 9.2, -0.6);
  for (let i = -1; i <= 1; i++) {
    b.cyl(0.35, 0.35, 1.8, M.tin, sx - 0.6 + i * 2.54, J.pcbY - 1.6, -9.5, 6);
    b.cyl(0.35, 0.35, 1.8, M.tin, sx + 9.5, J.pcbY - 1.6, -0.6 + i * 2.54, 6);
  }
  // Armazón: placa superior con abertura circular y dos patas.
  const frameShape = boardShape(17, 17, { r: 0.6 });
  const hole = new THREE.Path();
  hole.absarc(0, 0, 6.4, 0, Math.PI * 2, true);
  frameShape.holes.push(hole);
  const frame = new THREE.ExtrudeGeometry(frameShape, { depth: 0.6, bevelEnabled: false, curveSegments: 24 });
  frame.rotateX(-Math.PI / 2);
  b.add(frame, M.steel, { x: sx, y: top + 9.6, z: 0 });
  b.box(0.6, 5.0, 14, M.steel, sx - 8.2, top + 4.6, 0);
  b.box(14, 5.0, 0.6, M.steel, sx, top + 4.6, 8.2);
  b.box(4, 2.2, 3.2, mat('#e8e4d8', { roughness: 0.6 }), sx - 4, top + 4.6, 6.4); // pulsador SW
  b.build();

  // Palanca: domo del cardán, eje y capuchón; gira alrededor del centro del cardán.
  const pivotY = top + 9.4;
  const stick = new THREE.Group();
  stick.position.set(sx, pivotY, 0);
  b.root.add(stick);
  const capMat = new THREE.MeshStandardMaterial({ map: knurlTexture('joycap', '#232427', '#151618', 40, [2 / 7, 3 / 7]), roughness: 0.62, side: THREE.DoubleSide });
  const dome = new THREE.Mesh(sharedGeo('joydome', () => new THREE.SphereGeometry(5.9, 28, 12, 0, Math.PI * 2, 0, Math.PI / 2)), mat('#1c1d20', { roughness: 0.4 }));
  const shaft = new THREE.Mesh(sharedGeo('joyshaft', () => new THREE.CylinderGeometry(2.1, 2.4, 8.5, 18).translate(0, 8.0, 0)), mat('#2a2b2f', { roughness: 0.45 }));
  // Capuchón tipo PS2: borde con estrías y la cara superior cóncava.
  const capGeo = sharedGeo('joycap', () => {
    const pts = [[2.2, 0], [8.4, 0], [9.6, 0.9], [9.7, 5.4], [9.2, 6.6], [8.1, 7.1], [4.5, 6.4], [0, 6.1]].map(([r, y]) => new THREE.Vector2(r, y));
    return new THREE.LatheGeometry(pts, 40).translate(0, 11.6, 0);
  });
  const cap = new THREE.Mesh(capGeo, capMat);
  for (const m of [dome, shaft, cap]) {
    m.castShadow = true;
    m.receiveShadow = true;
    stick.add(m);
  }
  interactive(stick, { kind: 'stick', keyX: 'x', keyY: 'y', clickKey: 'click', label: 'Palanca (un clic sin arrastrar la hunde: SW)' });

  const up = new THREE.Vector3(0, 1, 0);
  const dir = new THREE.Vector3();
  const tan = Math.tan((J.maxTilt * Math.PI) / 180);
  let pressed = false;
  const setTilt = (x, y) => {
    dir.set((Number(x) || 0) * tan, 1, -(Number(y) || 0) * tan).normalize();
    stick.quaternion.setFromUnitVectors(up, dir);
  };
  return {
    root: b.root,
    setProps(p) {
      setTilt(p.x, p.y);
    },
    update(v) {
      pressed = !!v.sw;
      stick.position.y = pivotY - (pressed ? 0.8 : 0);
    },
    reset() {
      pressed = false;
      stick.position.y = pivotY;
    },
  };
}

// Encoder rotatorio KY-040 --------------------------------------------------------------------------
function buildEncoder() {
  const E = ENCODER;
  const b = new Builder();
  const top = E.top;
  const kx = E.knobX;
  const names = ['CLK', 'DT', 'SW', '+', 'GND'];
  const zs = names.map((_, i) => (i - 2) * PITCH);
  pcb(b, {
    key: 'ky040', w: E.w, d: E.d, y0: E.pcbY, color: '#1c4e9e', edge: '#143a77',
    holes: [[11.0, -6.8, 1.4], [11.0, 6.8, 1.4]],
    draw: (g, X, Y) => {
      names.forEach((n, i) => text(g, n, X(-10.2), Y(zs[i]), { size: 1.3, color: SILK, align: 'left', weight: 700 }));
      text(g, 'R1', X(-4.7), Y(-8.3), { size: 0.95, color: SILK });
      text(g, 'R2', X(-6.3), Y(8.4), { size: 0.95, color: SILK });
      text(g, 'R3', X(-4.0), Y(8.4), { size: 0.95, color: SILK });
      g.strokeStyle = SILK;
      g.lineWidth = 0.18;
      g.strokeRect(X(kx - 6.6), Y(-6.9), 13.2, 13.8);
    },
  });
  headerSide(b, zs, { bendX: -11.0, tipX: E.pinX, y: top + 1.27, top, dir: -1 });
  // R2 y R3: pull-ups de 10 kΩ de CLK y DT; R1 (pull-up de SW) viene sin soldar.
  smd(b, -6.3, 6.6, top, { rot: Math.PI / 2 });
  smd(b, -4.0, 6.6, top, { rot: Math.PI / 2 });
  emptyPads(b, -4.7, -6.6, top, { rot: Math.PI / 2 });
  // Cuerpo del encoder (EC11): base de plástico, tapa metálica con pestañas, buje roscado y tuerca.
  b.rbox(12.0, 3.8, 12.6, 0.4, M.blackPlastic, kx, top, 0);
  b.box(12.4, 1.5, 13.0, M.steel, kx, top + 3.8, 0);
  for (const s of [-1, 1]) b.box(0.4, 4.6, 2.6, M.steel, kx + s * 6.3, top + 0.7, 0);
  b.cyl(4.6, 4.6, 0.4, M.steel, kx, top + 5.3, 0, 28);
  b.cyl(5.1, 5.1, 2.0, M.metal, kx, top + 5.7, 0, 6);
  b.cyl(3.5, 3.5, 5.0, mat('#b9bcc1', { roughness: 0.35, metalness: 0.9 }), kx, top + 5.3, 0, 24);
  for (let i = 0; i < 3; i++) b.cyl(0.35, 0.35, 1.8, M.tin, kx - 2.5 + i * 2.5, E.pcbY - 1.6, -7.0, 6);
  b.build();

  // Eje moleteado con ranura: gira 18° por paso y baja al presionarlo.
  const baseY = top + 10.3;
  const shaft = new THREE.Group();
  shaft.position.set(kx, baseY, 0);
  b.root.add(shaft);
  const smooth = new THREE.Mesh(sharedGeo('encsmooth', () => new THREE.CylinderGeometry(3.0, 3.0, 3.2, 28).translate(0, 1.6, 0)), mat('#c3c6cb', { roughness: 0.3, metalness: 0.9 }));
  const knurl = new THREE.Mesh(sharedGeo('encknurl', () => new THREE.CylinderGeometry(3.0, 3.0, 7.0, 36).translate(0, 6.7, 0)), [
    new THREE.MeshStandardMaterial({ map: knurlTexture('enc', '#c9ccd1', '#8d9196', 18), roughness: 0.35, metalness: 0.85 }),
    mat('#c3c6cb', { roughness: 0.3, metalness: 0.9 }),
    mat('#c3c6cb', { roughness: 0.3, metalness: 0.9 }),
  ]);
  const slot = new THREE.Mesh(sharedGeo('encslot', () => new THREE.BoxGeometry(6.05, 0.5, 0.8).translate(0, 10.0, 0)), M.hole);
  for (const m of [smooth, knurl, slot]) {
    m.castShadow = true;
    shaft.add(m);
  }
  interactive(shaft, { kind: 'encoder', key: 'press', label: 'Perilla del encoder' });

  let target = 0;
  let shown = 0;
  const apply = () => {
    shaft.rotation.y = -(shown * Math.PI) / 180;
  };
  return {
    root: b.root,
    update(v) {
      target = (v.pos || 0) * (360 / E.detents);
      shaft.position.y = baseY - (v.pressed ? 0.6 : 0);
    },
    animate(dt) {
      const d = target - shown;
      if (Math.abs(d) < 0.05) {
        shown = target;
        apply();
        return false;
      }
      shown += d * Math.min(1, dt * 18);
      apply();
      return true;
    },
    reset() {
      shaft.position.y = baseY;
    },
  };
}

// Teclas con rótulo (teclados de membrana y control remoto) -----------------------------------------
/** Tecla plana con esquinas redondeadas en planta y canto suave (base en y = 0, alto h). */
function keyGeometry(key, w, d, h, r) {
  return sharedGeo('key:' + key, () => {
    const b = Math.min(0.15, h / 4);
    const g = new THREE.ExtrudeGeometry(boardShape(w - 2 * b, d - 2 * b, { r }), {
      depth: h - 2 * b, bevelEnabled: true, bevelThickness: b, bevelSize: b, bevelSegments: 2, curveSegments: 8,
    });
    g.rotateX(-Math.PI / 2);
    g.translate(0, b, 0);
    return g;
  });
}

// Íconos de reproducción dibujados a mano (no dependen de las fuentes instaladas).
function tri(g, x, y, w, h, dir) {
  g.beginPath();
  g.moveTo(x + (dir > 0 ? w : 0), y);
  g.lineTo(x + (dir > 0 ? 0 : w), y - h / 2);
  g.lineTo(x + (dir > 0 ? 0 : w), y + h / 2);
  g.closePath();
  g.fill();
}
const ICONS = {
  '⏮': (g, x, y) => {
    g.fillRect(x - 0.27, y - 0.13, 0.05, 0.26);
    tri(g, x - 0.22, y, 0.2, 0.26, -1);
    tri(g, x - 0.02, y, 0.2, 0.26, -1);
  },
  '⏭': (g, x, y) => {
    tri(g, x - 0.24, y, 0.2, 0.26, 1);
    tri(g, x - 0.04, y, 0.2, 0.26, 1);
    g.fillRect(x + 0.17, y - 0.13, 0.05, 0.26);
  },
  '⏯': (g, x, y) => {
    tri(g, x - 0.26, y, 0.22, 0.28, 1);
    g.fillRect(x + 0.04, y - 0.14, 0.07, 0.28);
    g.fillRect(x + 0.16, y - 0.14, 0.07, 0.28);
  },
};

/**
 * Atlas con los rótulos de un juego de teclas y una geometría por tecla con las coordenadas de su
 * celda: todas las etiquetas comparten material.
 */
function keyLabels(key, labels, { cell = 64, size = 0.62, color = '#ffffff', font = 'Arial, Helvetica, sans-serif' } = {}) {
  const cols = 8;
  const rows = Math.ceil(labels.length / cols);
  const tex = sharedTexture('keys:' + key, cols, rows, cell, (g) => {
    labels.forEach((l, i) => {
      const cx = (i % cols) + 0.5;
      const cy = Math.floor(i / cols) + 0.5;
      if (ICONS[l]) {
        g.fillStyle = color;
        ICONS[l](g, cx, cy);
        return;
      }
      const s = l.length > 2 ? size * 0.62 : size;
      text(g, l, cx, cy + 0.03, { size: s, color, weight: 700, font });
    });
  });
  const material = new THREE.MeshStandardMaterial({ map: tex, transparent: true, alphaTest: 0.25, roughness: 0.5, depthWrite: false });
  const geoFor = (i, w, d) =>
    sharedGeo(`keylbl:${key}:${i}:${w}x${d}`, () => {
      const g = new THREE.PlaneGeometry(w, d).rotateX(-Math.PI / 2);
      const uv = g.attributes.uv;
      const c = i % cols;
      const r = Math.floor(i / cols);
      for (let k = 0; k < uv.count; k++) {
        uv.setXY(k, (c + uv.getX(k)) / cols, 1 - (r + 1 - uv.getY(k)) / rows);
      }
      return g;
    });
  return { material, geoFor };
}

// Teclados de membrana 4×4 y 3×4 -------------------------------------------------------------------
const KEY_COLORS = { digit: '#2a55b0', letter: '#c3302c', symbol: '#3b4250' };

function buildKeypad(def) {
  const L = keypadLayout(def.type);
  const b = new Builder();
  const T = KEYPAD.t;
  const names = keypadPins(L);
  const n = names.length;
  // Cuerpo delgado con el marco impreso
  const face = sharedTexture('kpface:' + def.type, L.w, L.d, 8, (g) => {
    g.fillStyle = '#121214';
    g.fillRect(0, 0, L.w, L.d);
    g.strokeStyle = '#3a3c42';
    g.lineWidth = 0.5;
    g.strokeRect(1.6, 1.6, L.w - 3.2, L.d - 3.2);
  });
  b.root.add(extrudeBoard(boardShape(L.w, L.d, { r: 2.2 }), T, face, '#0e0e10', { w: L.w, d: L.d, y0: 0, roughness: 0.32 }));
  // Cinta flexible con las pistas y el conector hembra
  const cw = n * PITCH + 1.4;
  const z0 = L.d / 2 - 1;
  const z1 = L.d / 2 + KEYPAD.cable;
  const tail = sharedTexture('kptail:' + n, cw, 8, 16, (g) => {
    g.fillStyle = '#d8d4c8';
    g.fillRect(0, 0, cw, 8);
    g.fillStyle = '#8e939a';
    for (let i = 0; i < n; i++) g.fillRect(0.7 + i * PITCH + PITCH / 2 - 0.5, 0, 1.0, 8);
  });
  const tailMesh = new THREE.Mesh(new THREE.PlaneGeometry(cw, z1 - z0).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: tail, roughness: 0.45, side: THREE.DoubleSide }));
  tailMesh.position.set(0, 0.12, (z0 + z1) / 2);
  tailMesh.receiveShadow = true;
  b.root.add(tailMesh);
  const zEnd = keypadConnectorZ(L);
  b.box(n * PITCH + 0.4, KEYPAD.connH, KEYPAD.conn, M.blackPlastic, 0, 0, zEnd - KEYPAD.conn / 2);
  b.box(n * PITCH - 1, 0.5, 3.5, M.blackPlastic, 0, KEYPAD.connH, zEnd - KEYPAD.conn + 2.5);
  for (let i = 0; i < n; i++) b.box(1.0, 1.0, 0.1, M.hole, (i - (n - 1) / 2) * PITCH, KEYPAD.connH / 2 - 0.5, zEnd + 0.01);
  b.build();

  // Teclas
  const labels = keyLabels('kp:' + def.type, L.keys, { size: 0.6 });
  const keyGeo = keyGeometry('kp' + L.key, L.key, L.key, 0.5, 1.8);
  const keyMats = {};
  const keys = L.keys.map((k, i) => {
    const r = Math.floor(i / L.cols);
    const c = i % L.cols;
    const kind = /[0-9]/.test(k) ? 'digit' : /[A-D]/.test(k) ? 'letter' : 'symbol';
    const m = keyMats[kind] || (keyMats[kind] = mat(KEY_COLORS[kind], { roughness: 0.4 }));
    const key = new THREE.Mesh(keyGeo, m);
    key.position.set((c - (L.cols - 1) / 2) * L.pitchX, T, L.y0 + (r - (L.rows - 1) / 2) * L.pitchZ);
    key.castShadow = true;
    key.receiveShadow = true;
    const lbl = new THREE.Mesh(labels.geoFor(i, L.key, L.key), labels.material);
    lbl.position.y = 0.52;
    lbl.renderOrder = 2;
    key.add(lbl);
    interactive(key, { kind: 'key', key: 'k' + i, value: k, label: `Tecla ${k}` });
    b.root.add(key);
    return key;
  });
  const down = new Set();
  const apply = (set) => keys.forEach((m, i) => (m.position.y = T - (set.has(L.keys[i]) || down.has(i) ? 0.3 : 0)));
  let simDown = new Set();
  return {
    root: b.root,
    press(key, isDown) {
      const i = Number(String(key).slice(1));
      if (!Number.isInteger(i)) return;
      if (isDown) down.add(i);
      else down.delete(i);
      apply(simDown);
    },
    update(v) {
      simDown = new Set(v.down || []);
      apply(simDown);
    },
    reset() {
      simDown = new Set();
      down.clear();
      apply(simDown);
    },
  };
}

// Receptor IR VS1838B con control remoto «Car MP3» ------------------------------------------------
const REMOTE_ROWS = ['#b3332f', '#2b52a6', '#6b3fa0', '#3a3d44', '#3a3d44', '#3a3d44', '#3a3d44'];

function buildIrReceiver() {
  const b = new Builder();
  // Receptor: patas planas, cuerpo de epoxi negro y la lente en domo hacia el control.
  for (const x of [-PITCH, 0, PITCH]) b.box(0.5, 5.2, 0.4, M.tin, x, 0, 0);
  const epoxy = mat('#1a1a1d', { roughness: 0.35 });
  b.rbox(6.0, 6.8, 3.4, 0.5, epoxy, 0, 5.0, -0.3);
  b.add(sharedGeo('irdome', () => new THREE.SphereGeometry(2.5, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2)), mat('#141416', { roughness: 0.18 }), { x: 0, y: 8.7, z: 1.4, rx: Math.PI / 2, sy: 0.85 });

  // Control remoto (delante del receptor, apuntándole con su LED)
  const R = IR_REMOTE;
  const zc = R.z0 + R.l / 2;
  const y = R.y;
  b.rbox(R.w, R.h, R.l, 2.6, mat('#18181b', { roughness: 0.4 }), 0, y, zc);
  b.box(R.w - 6, 0.6, 9, mat('#0f0f11', { roughness: 0.5 }), 0, y - 0.4, zc + R.l / 2 - 10); // tapa de la pila
  b.build();
  const faceTex = sharedTexture('irface', R.w, R.l, 12, (g) => {
    g.clearRect(0, 0, R.w, R.l);
    g.strokeStyle = 'rgba(210,214,222,0.55)';
    g.lineWidth = 0.35;
    g.strokeRect(3.2, 6.5, R.w - 6.4, 66);
    text(g, 'Car mp3', R.w / 2, R.l - 8, { size: 4.6, color: '#e7e9ee', weight: 700, font: 'Georgia, serif' });
  });
  const face = new THREE.Mesh(new THREE.PlaneGeometry(R.w, R.l).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: faceTex, transparent: true, roughness: 0.45, depthWrite: false }));
  face.position.set(0, y + R.h + 0.02, zc);
  face.userData.noPick = true;
  b.root.add(face);
  // LED infrarrojo en la punta
  const ledMat = new THREE.MeshStandardMaterial({ color: '#3b3550', emissive: '#a070ff', emissiveIntensity: 0, roughness: 0.15, transparent: true, opacity: 0.9 });
  const led = new THREE.Mesh(sharedGeo('irled', () => new THREE.SphereGeometry(2.2, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2)), ledMat);
  led.position.set(0, y + R.h / 2, R.z0);
  led.rotation.x = -Math.PI / 2;
  led.scale.set(1, 0.9, 1);
  b.root.add(led);
  const halo = glow('#b48cff', 9);
  halo.position.set(0, y + R.h / 2, R.z0 - 1.5);
  b.root.add(halo);
  // Teclas de membrana: 7 filas de 3
  const labels = keyLabels('irremote', IR_REMOTE_KEYS.map((k) => k.label), { size: 0.52 });
  const keyGeo = keyGeometry('ir', 10.4, 7.2, 1.0, 1.6);
  const keyMats = REMOTE_ROWS.map((c) => mat(c, { roughness: 0.45 }));
  const keys = IR_REMOTE_KEYS.map((k, i) => {
    const r = Math.floor(i / 3);
    const c = i % 3;
    const key = new THREE.Mesh(keyGeo, keyMats[r]);
    key.position.set((c - 1) * 12.2, y + R.h - 0.35, R.z0 + 12 + r * 9.4);
    key.castShadow = true;
    const lbl = new THREE.Mesh(labels.geoFor(i, 7.2, 7.2), labels.material);
    lbl.position.y = 1.02;
    lbl.renderOrder = 2;
    key.add(lbl);
    interactive(key, { kind: 'key', key: 'r' + i, value: k.label, label: `Control: ${k.label}` });
    b.root.add(key);
    return key;
  });
  const baseY = y + R.h - 0.35;
  let held = null;
  const local = new Set();
  const apply = () => keys.forEach((m, i) => (m.position.y = baseY - (local.has(i) || IR_REMOTE_KEYS[i].label === held ? 0.45 : 0)));
  const setTx = (on) => {
    ledMat.emissiveIntensity = on ? 2.2 : 0;
    setGlow(halo, on ? 0.45 : 0);
  };
  setTx(false);
  return {
    root: b.root,
    press(key, isDown) {
      const i = Number(String(key).slice(1));
      if (!Number.isInteger(i)) return;
      if (isDown) local.add(i);
      else local.delete(i);
      apply();
    },
    update(v) {
      held = v.key || null;
      apply();
      setTx(!!v.tx);
    },
    reset() {
      held = null;
      local.clear();
      apply();
      setTx(false);
    },
  };
}

// Báscula: módulo HX711 y celda de carga de barra ----------------------------------------------------
function buildHx711(def, props) {
  const H = HX711_BOARD;
  const C = LOAD_CELL;
  const b = new Builder();
  const top = H.top;
  const names = ['GND', 'DT', 'SCK', 'VCC'];
  const cellPads = ['E+', 'E-', 'A-', 'A+', 'B-', 'B+'];
  const zs = names.map((_, i) => (i - 1.5) * PITCH);
  const pz = cellPads.map((_, i) => (i - 2.5) * PITCH);
  pcb(b, {
    key: 'hx711', w: H.w, d: H.d, y0: H.pcbY, color: '#1d7b3d', edge: '#155a2c',
    draw: (g, X, Y) => {
      names.forEach((n, i) => text(g, n, X(-12.6), Y(zs[i]), { size: 1.3, color: SILK, align: 'left', weight: 700 }));
      cellPads.forEach((n, i) => text(g, n, X(12.7), Y(pz[i]), { size: 1.2, color: SILK, align: 'right', weight: 700 }));
      text(g, 'HX711', X(0.5), Y(-7.6), { size: 1.5, color: SILK, weight: 700 });
      g.strokeStyle = SILK;
      g.lineWidth = 0.18;
      g.strokeRect(X(-16.2), Y(-5.6), 2.6, 11.2);
    },
  });
  headerUp(b, zs.map((z) => [H.pinX, z]), { y: top, tipY: H.pinY });
  for (const z of pz) pad(b, 14.9, z, top, { r: 0.95, hole: 0.45 });
  soic(b, b.root, 0.5, -0.8, top, { pins: 16, w: 9.9, d: 3.9, h: 1.45, lines: ['HX711', 'AVIA'] });
  sot23(b, -8.4, 5.6, top, { rot: Math.PI / 2 });
  const parts = [[-8.0, -5.5, 0], [-5.2, 5.8, Math.PI / 2], [6.6, 5.4, 0], [9.2, 5.4, 0], [7.6, -6.4, 0], [-2.6, 6.2, 0], [2.6, 6.2, 0]];
  parts.forEach(([x, z, r], i) => smd(b, x, z, top, { rot: r, color: i % 3 === 1 ? '#b59a72' : '#1b1b1d' }));
  b.box(3.4, 1.6, 1.8, mat('#c9a43a', { roughness: 0.45 }), -3.0, top, -6.6); // capacitor de tantalio
  for (let i = 0; i < 3; i++) b.cyl(0.35, 0.35, 1.6, M.tin, -12 + i * 12, 0, 8.6, 6); // patas de apoyo

  // Celda de carga: barra de aluminio sobre un separador, con la galga cubierta de silicón al centro.
  const alu = mat('#b8bcc2', { roughness: 0.42, metalness: 0.75 });
  const s = C.side;
  const x0 = C.x0;
  const x1 = C.x0 + C.len;
  const yb = C.y0;
  b.box(15, yb, s, mat('#7d8188', { roughness: 0.45, metalness: 0.7 }), x0 + 7.5, 0, 0);
  b.rbox(C.len, s, s, 0.6, alu, (x0 + x1) / 2, yb, 0);
  for (const hx of [x0 + 5, x0 + 15, x1 - 5, x1 - 15]) b.cyl(1.9, 1.9, 0.1, M.hole, hx, yb + s, 0, 18);
  // Agujero binocular del centro, en las dos caras laterales
  const hole = new THREE.Shape();
  hole.absarc(-5.6, 0, 4.4, Math.PI / 2, (3 * Math.PI) / 2, false);
  hole.lineTo(5.6, -4.4);
  hole.absarc(5.6, 0, 4.4, -Math.PI / 2, Math.PI / 2, false);
  hole.lineTo(-5.6, 4.4);
  const holeGeo = new THREE.ShapeGeometry(hole, 16);
  for (const sz of [-1, 1]) b.add(holeGeo, M.hole, { x: (x0 + x1) / 2, y: yb + s / 2, z: sz * (s / 2 + 0.02), ry: sz < 0 ? Math.PI : 0 });
  b.rbox(16, 1.4, 9, 0.6, mat('#ecebe4', { roughness: 0.7 }), (x0 + x1) / 2, yb + s - 0.2, 0);
  // Separador y plataforma en el extremo libre
  b.box(15, 5, s, mat('#7d8188', { roughness: 0.45, metalness: 0.7 }), x1 - 7.5, yb + s, 0);
  const platX = x1 - 7.5;
  const platY = yb + s + 5;
  b.cyl(20, 20, 3, mat('#d9dde2', { roughness: 0.3, metalness: 0.5 }), platX, platY, 0, 48);
  // Cables de la celda: rojo E+, negro E−, blanco A−, verde A+
  const wireCol = ['#d23a2c', '#1b1b1d', '#efefe9', '#21a04a'];
  cable(b, [[x0 + 0.5, yb + s / 2, 0], [x0 - 4, yb + s / 2, 0], [x0 - 7, yb + 2.5, 0]], 1.5, '#2b2c30');
  wireCol.forEach((c, i) => {
    const z = pz[i];
    cable(b, [[x0 - 7, yb + 2.5, (i - 1.5) * 0.9], [x0 - 10, yb + 1.0, z * 0.6], [17.5, top + 1.6, z], [14.9, top + 0.5, z]], 0.55, c);
  });
  b.build();

  // Rótulo de la celda (capacidad y flecha de la dirección de la carga)
  const cap = HX711_CELLS[props.capacity] ? props.capacity : 1000;
  const sticker = sharedTexture('cell:' + cap, 18, 10, 24, (g) => {
    g.fillStyle = '#eceae2';
    g.fillRect(0, 0, 18, 10);
    text(g, HX711_CELLS[cap].label, 6.6, 5, { size: 4.2, color: '#1b1b1d', weight: 800 });
    g.fillStyle = '#1b1b1d';
    g.beginPath();
    g.moveTo(14.5, 8.4);
    g.lineTo(12.4, 5.2);
    g.lineTo(13.8, 5.2);
    g.lineTo(13.8, 1.6);
    g.lineTo(15.2, 1.6);
    g.lineTo(15.2, 5.2);
    g.lineTo(16.6, 5.2);
    g.closePath();
    g.fill();
  });
  const stickerMesh = new THREE.Mesh(new THREE.PlaneGeometry(18, 10), new THREE.MeshStandardMaterial({ map: sticker, roughness: 0.6 }));
  stickerMesh.position.set(x1 - 22, yb + s / 2, s / 2 + 0.03);
  b.root.add(stickerMesh);

  // Pesa sobre la plataforma (tamaño según el peso)
  const brass = mat('#c8a24a', { roughness: 0.3, metalness: 0.9 });
  const weight = new THREE.Group();
  const wb = new THREE.Mesh(sharedGeo('wbody', () => new THREE.CylinderGeometry(9, 9, 11, 32).translate(0, 5.5, 0)), brass);
  const wn = new THREE.Mesh(sharedGeo('wneck', () => new THREE.CylinderGeometry(4.2, 5.2, 2.6, 24).translate(0, 12.3, 0)), brass);
  const wk = new THREE.Mesh(sharedGeo('wknob', () => new THREE.SphereGeometry(5.6, 24, 14).scale(1, 0.62, 1).translate(0, 15.6, 0)), brass);
  for (const m of [wb, wn, wk]) {
    m.castShadow = true;
    weight.add(m);
  }
  weight.position.set(platX, platY + 3, 0);
  b.root.add(weight);
  const setWeight = (g) => {
    g = Number(g) || 0;
    weight.visible = g >= 1;
    const k = Math.min(1.7, Math.max(0.38, Math.cbrt(g / 1000)));
    weight.scale.setScalar(k);
  };
  setWeight(props.weight);
  return {
    root: b.root,
    setProps(p) {
      setWeight(p.weight);
    },
  };
}

export const MODELS = {
  joystick: buildJoystick,
  'rotary-encoder': buildEncoder,
  keypad: buildKeypad,
  'keypad-3x4': buildKeypad,
  'ir-receiver': buildIrReceiver,
  hx711: buildHx711,
  ...I2C,
};
